import React from 'react';
import type { NavPage } from '../utils/routes';
import { SignInButton, SignUpButton } from '@clerk/react';
import { welcomeRedirectUrl } from '../utils/signupConversion';
import Header from './Header';
import BatchNavBar from './BatchNavBar';
import PostPurchaseBanner from './PostPurchaseBanner';
import ScanResultsPage from '../pages/ScanResultsPage';
import { ScanResult } from '../utils/types';
import { useCreditInfo } from '../hooks/useCreditInfo';
import type { UnlockReturnStatus } from '../hooks/useUnlockReturn';
import type { SignedOutUnlock } from './PaywallOffer';
import { T } from '../designTokens';
import { clerkAppearance } from '../clerkAppearance';

interface ScanResultRouteProps {
  result: ScanResult;
  allBatchResults: ScanResult[];
  batchResultIndex: number;
  showPaymentBanner: boolean;
  purchasedScanId: string | null;
  /** The purchase behind purchasedScanId unlocked that report (#1762). */
  purchaseUnlockedReport?: boolean;
  /** Back from an unlock checkout, waiting on the webhook's unlock (#1776). */
  unlockReturn?: { status: UnlockReturnStatus; retry: () => void };
  reportId: string | null;
  userId?: string;
  isSignedIn: boolean;
  authEnabled: boolean;
  queueCount: number;
  creditInfo: ReturnType<typeof useCreditInfo>;
  onBillingClick: () => void;
  /** Header's onNavigate, already wrapped by the caller to clear this route's
   * batch/result/banner state before delegating to navigateTo (#416). */
  onNavigate: (page: NavPage) => void;
  onRunFullAudit: (url: string) => Promise<void> | void;
  /** Reload the result after its locked full audit was unlocked in place (#1762). */
  onUnlocked?: () => void;
  /**
   * #1779: set only for the signed-out visitor holding this scan's claim
   * identity. Their locked full audit then offers "Unlock this report".
   */
  signedOutUnlock?: SignedOutUnlock;
  onBack: () => void;
  onBatchNav: (newIndex: number) => void;
  onDismissPaymentBanner: () => void;
  onDismissPurchasedBanner: () => void;
}

// The scan-result route (#/scan/:id, #/report/:id once loaded) — Header +
// optional batch nav / purchase banners + ScanResultsPage itself. Extracted
// verbatim out of App()'s `if (currentResult)` branch (issue #416); all
// state mutation stays in App.tsx, handed down as the callbacks above, so
// behavior (including App.shell.test.tsx's "clears the current scan
// result when a Header nav button is clicked" coverage) is unchanged.
export default function ScanResultRoute({
  result,
  allBatchResults,
  batchResultIndex,
  showPaymentBanner,
  purchasedScanId,
  purchaseUnlockedReport = false,
  unlockReturn,
  reportId,
  userId,
  isSignedIn,
  authEnabled,
  queueCount,
  creditInfo,
  onBillingClick,
  onNavigate,
  onRunFullAudit,
  onUnlocked,
  signedOutUnlock,
  onBack,
  onBatchNav,
  onDismissPaymentBanner,
  onDismissPurchasedBanner,
}: ScanResultRouteProps) {
  const isBatch = allBatchResults.length > 1;

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: T.gray50 }}>
      <Header
        currentPage="scanner"
        onNavigate={onNavigate}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditBalance={creditInfo.balance}
        creditTier={creditInfo.tier}
        creditLoading={creditInfo.loading}
        creditHistory={creditInfo.hasCreditHistory}
        onBillingClick={onBillingClick}
      />
      {showPaymentBanner && (
        <PostPurchaseBanner variant="bar" onDismiss={onDismissPaymentBanner} />
      )}
      {isBatch && (
        <BatchNavBar
          currentIndex={batchResultIndex}
          total={allBatchResults.length}
          onNav={onBatchNav}
        />
      )}
      {unlockReturn && (unlockReturn.status === 'waiting' || unlockReturn.status === 'timeout') && (
        <UnlockReturnNotice status={unlockReturn.status} onRetry={unlockReturn.retry} />
      )}
      {purchasedScanId && result.id === purchasedScanId && (
        <PostPurchaseBanner
          variant="card"
          reportUnlocked={purchaseUnlockedReport}
          onDismiss={onDismissPurchasedBanner}
        />
      )}
      <ScanResultsPage
        result={result}
        onBack={onBack}
        reportId={reportId}
        userId={userId}
        isSignedIn={isSignedIn}
        authEnabled={authEnabled}
        balance={creditInfo.balance}
        creditsLoading={creditInfo.loading}
        packPrice={creditInfo.packPrice}
        creditsPerPurchase={creditInfo.creditsPerPurchase}
        hasCreditHistory={creditInfo.hasCreditHistory}
        onRunFullAudit={onRunFullAudit}
        onUnlocked={onUnlocked}
        signedOutUnlock={signedOutUnlock}
        signInButton={
          <>
            <SignUpButton
              mode="modal"
              forceRedirectUrl={welcomeRedirectUrl(`#/scan/${encodeURIComponent(result.id)}`)}
              appearance={clerkAppearance}
            >
              <button className="legacy-cta__button">Create free account to claim</button>
            </SignUpButton>
            <SignInButton mode="modal" appearance={clerkAppearance}>
              <button
                style={{
                  display: 'block',
                  margin: '12px auto 0',
                  padding: '4px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: T.gray400,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: T.fontBody,
                }}
              >
                Already have an account? Sign in
              </button>
            </SignInButton>
          </>
        }
      />
    </div>
  );
}

/**
 * #1776: the payment went through and the webhook is unlocking the report.
 * Says nothing about credits: an unlock buyer never bought any.
 */
function UnlockReturnNotice({ status, onRetry }: { status: 'waiting' | 'timeout'; onRetry: () => void }) {
  return (
    <div
      role="status"
      data-testid="unlock-return-notice"
      style={{
        backgroundColor: T.white,
        border: `1px solid ${T.gray200}`,
        borderRadius: '6px',
        padding: '12px 16px',
        margin: '16px auto 0',
        maxWidth: '960px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        fontFamily: T.fontBody,
        fontSize: '14px',
        color: T.gray700,
      }}
    >
      <span>
        {status === 'waiting'
          ? 'Payment received. Unlocking your report…'
          : 'Payment received. Your report is still unlocking; this usually takes a moment.'}
      </span>
      {status === 'timeout' && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            font: 'inherit',
            fontWeight: 600,
            color: T.cobalt600,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          Check again
        </button>
      )}
    </div>
  );
}
